import React, { useState } from "react";
import {
  FieldType,
  FieldTypeType,
  OptionType,
} from "../../../services/helpers/types";
import { Divider } from "@mui/material";
import DraggableSelectOptionsList from "../DraggableSelectOptionsList/DraggableSelectOptionsList";
import Button from "../Button/Button";
import { Add } from "@mui/icons-material";
import MultiSelect from "../../molecules/MultiSelect/MultiSelect";
import {
  getHighestNumberInOptionValues,
  isThisOptionDefaultValue,
} from "../../../services/helpers/commonHelpers";
import { useTranslation } from "react-i18next";

interface AddMultiSelectFieldProps {
  field?: FieldType;
  onFieldChange: (name: string, value?: any, defaultValue?: any) => void;
}

const AddMultiSelectField = (props: AddMultiSelectFieldProps) => {
  const { field, onFieldChange } = props;

  const { t } = useTranslation();

  const [defaultValue, setDefaultValue] = React.useState<string | undefined>(
    field?.defaultValue ?? undefined,
  );

  const [newOptions, setNewOptions] = useState<OptionType[]>(
    field?.options ?? [],
  );

  const [customField, setCustomField] = useState<FieldType | undefined>(field);

  const handleDefaultValueChange = (value?: string) => {
    setDefaultValue(value);
    onFieldChange("defaultValue", value);
  };

  const setDefaultValueSelectOptions = (
    options: OptionType[],
    newDefaultValue?: string,
  ) => {
    if (customField) {
      setCustomField({
        ...customField,
        options: options,
      });
    } else {
      setCustomField({
        fieldID: 0,
        name: "Možnosti",
        position: 0,
        type: FieldTypeType.MultipleChoice,
        noteOption: false,
        defaultValue: defaultValue ?? "",
        options: options,
        tabID: 0,
        placeholder: "",
        value: "",
      });
    }
    if (newDefaultValue !== undefined) {
      setDefaultValue(newDefaultValue);
    }
    onFieldChange("options", options, newDefaultValue);
  };

  const handleAddField = () => {
    const highestNumberInOptionValues = getHighestNumberInOptionValues(
      newOptions,
      t,
    );
    const addNewOptions: OptionType[] = [
      ...newOptions,
      {
        name: `${t("newOption")} ${highestNumberInOptionValues + 1}`,
        value: `${t("newOption")} ${highestNumberInOptionValues + 1}`,
        position: newOptions.length + 1,
        optionID: 0,
        fieldID: 0,
      },
    ];

    setDefaultValueSelectOptions(addNewOptions);
    setNewOptions(addNewOptions);
  };

  const handleDelete = (itemID: number) => {
    const currentOption = newOptions.find((value) => value.position === itemID);
    // fallback if user somehow deletes option that doesn't exist
    if (!currentOption) {
      return;
    }
    const deleteNewOptions = newOptions
      .filter((option) => option.position !== itemID)
      .map((value, index) => {
        return { ...value, position: index + 1 };
      });

    if (
      isThisOptionDefaultValue(currentOption, newOptions, defaultValue) &&
      defaultValue
    ) {
      const newDefaultValueArray = defaultValue.split("|");
      const newDefaultValue = newDefaultValueArray.filter(
        (value) => value !== currentOption.value,
      );
      const newDefaultValueString = newDefaultValue.join("|");
      setDefaultValueSelectOptions(deleteNewOptions, newDefaultValueString);
    } else {
      setDefaultValueSelectOptions(deleteNewOptions);
    }
    setNewOptions(deleteNewOptions);
  };

  const handleOptionChange = (option: OptionType) => {
    const position = option.position;
    const oldOption = newOptions.find((value) => value.position === position);

    // if option is already selected and value is changed, change default value too
    if (oldOption) {
      if (oldOption.value !== option.value && defaultValue) {
        const defaultValueArray = defaultValue.split("|");
        if (defaultValueArray.includes(oldOption.value)) {
          const newDefaultValueArray = defaultValueArray.map((value) => {
            if (value === oldOption.value) {
              return option.value;
            }
            return value;
          });
          const newDefaultValue = newDefaultValueArray.join("|");

          const changeNewOptions = newOptions.map((value) => {
            if (value.position === option.position) {
              return option;
            }
            return value;
          });
          setDefaultValue(newDefaultValue);
          setDefaultValueSelectOptions(changeNewOptions, newDefaultValue);
          setNewOptions(changeNewOptions);
          return;
        }
      }
    }

    const changeNewOptions = newOptions.map((value) => {
      if (value.position === option.position) {
        return option;
      }
      return value;
    });

    setDefaultValueSelectOptions(changeNewOptions);
    setNewOptions(changeNewOptions);
  };

  const handlePositionChange = (newItemsPositions: number[]) => {
    const reorderedNewOptions: OptionType[] = newItemsPositions.map(
      (position, index) => {
        const newOption = newOptions.find(
          (option) => option.position === position,
        );
        if (!newOption) {
          return newOptions[index];
        }
        return { ...newOption, position: index + 1 };
      },
    );
    setNewOptions(reorderedNewOptions);
    onFieldChange("options", reorderedNewOptions);
  };

  return (
    <div className="dui-add-multi-select-field">
      <Divider className="dui-divider" />
      <div className="dui-add-multi-select-field-values">
        <div className="dui-add-multi-select-field-values-title">
          {t("selectOptions")}:
        </div>
        <DraggableSelectOptionsList
          options={newOptions}
          onDeleteItem={handleDelete}
          onOptionChange={handleOptionChange}
          onChangePosition={handlePositionChange}
        />
        <div
          className="dui-fields-edit-add-button-wrapper"
          onClick={handleAddField}
        >
          <Button
            icon={<Add />}
            type="primary"
            corners="circle"
            className="dui-fields-edit-add-button"
          />
        </div>
      </div>
      <Divider className="dui-divider" />
      <div className="dui-example-field">
        <div className="dui-example-field-title">{t("defaultValue")}:</div>
        <MultiSelect
          field={customField}
          value={defaultValue}
          onChange={handleDefaultValueChange}
        />
      </div>
    </div>
  );
};

export default AddMultiSelectField;
